import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Building2, 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle,
  Loader2,
  Upload,
  FileText,
  Users,
  DollarSign,
  Shield,
  Scale,
  Briefcase,
  X,
  File,
  AlertCircle,
  MapPin,
  Phone,
  Mail,
  Calendar,
  ClipboardCheck
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOAN_PRODUCTS = [
  { id: "working_capital", name: "Working Capital", description: "Short-term funding for daily operations" },
  { id: "invoice_discounting", name: "Invoice Discounting", description: "Advance against unpaid invoices" },
  { id: "merchant_cash_advance", name: "Merchant Cash Advance", description: "Based on future card sales" },
  { id: "term_loan", name: "Term Loan", description: "Fixed amount with scheduled repayments" },
  { id: "home_equity", name: "Home Equity Loans", description: "Leverage your property equity for financing" },
  { id: "asset_finance", name: "Asset Finance", description: "Equipment and machinery financing" }
];

const BUSINESS_TYPES = [
  "Retail/Trading",
  "Manufacturing",
  "Services",
  "Agriculture",
  "Construction",
  "Transport",
  "Hospitality",
  "Healthcare",
  "Technology",
  "Other"
];

const DOCUMENT_CATEGORIES = [
  { id: "company_registration", name: "Company Registration Certificate", required: true },
  { id: "cr14", name: "CR14 (Directors Details)", required: true },
  { id: "cr6", name: "CR6 (Registered Office)", required: true },
  { id: "directors_ids", name: "Directors National IDs", required: true },
  { id: "proof_of_residence", name: "Proof of Residence", required: true },
  { id: "bank_statements", name: "Bank Statements (6 months)", required: true },
  { id: "financial_statements", name: "Financial Statements / Management Accounts", required: true },
  { id: "tax_clearance", name: "Tax Clearance Certificate", required: true },
  { id: "business_profile", name: "Business Profile / Plan", required: false },
  { id: "quotations", name: "Quotations / Proforma Invoices", required: false },
  { id: "contracts", name: "Contracts / Purchase Orders", required: false },
  { id: "collateral_docs", name: "Collateral Documentation", required: false },
  { id: "other", name: "Other Supporting Documents", required: false }
];

const BusinessLoanApplication = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [documents, setDocuments] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    // Application Info
    branch: "",
    officer: "",
    application_date: new Date().toISOString().split('T')[0],
    
    // Business Information
    business_name: "",
    trading_name: "",
    company_reg_number: "",
    date_incorporated: "",
    business_type: "",
    industry_sector: "",
    registered_address: "",
    physical_address: "",
    property_ownership: "",
    telephone: "",
    mobile: "",
    email: "",
    website: "",
    
    // Directors/Owners
    directors: [{
      full_name: "",
      id_number: "",
      position: "",
      phone: "",
      email: "",
      residential_address: "",
      ownership_percentage: ""
    }],
    
    // Contact Person
    contact_person_name: "",
    contact_person_position: "",
    contact_person_phone: "",
    contact_person_email: "",
    
    // Loan Details
    loan_product: "",
    amount_required: "",
    tenure_months: "",
    custom_tenure: "",
    purpose_of_loan: "",
    repayment_source: "",
    preferred_disbursement: "",
    
    // Business Financials
    annual_revenue: "",
    monthly_revenue: "",
    gross_profit_margin: "",
    monthly_expenses: "",
    net_profit: "",
    number_of_employees: "",
    years_in_business: "",
    
    // Existing Obligations
    has_existing_loans: "no",
    existing_loans: [{
      lender: "",
      loan_type: "",
      original_amount: "",
      current_balance: "",
      monthly_repayment: "",
      maturity_date: ""
    }],
    
    // Banking Information
    bank_name: "",
    branch_name: "",
    account_number: "",
    account_type: "",
    years_with_bank: "",
    
    // Security/Collateral
    collateral_offered: "yes",
    collateral_items: [{
      asset_type: "",
      description: "",
      registered_owner: "",
      registration_number: "",
      estimated_value: "",
      location: ""
    }],
    
    // References
    trade_references: [{
      company_name: "",
      contact_person: "",
      phone: "",
      relationship: "",
      years_known: ""
    }],
    
    // Declaration & Consent
    declaration_agreed: false,
    terms_agreed: false,
    consent_identity_verification: false,
    consent_credit_bureau: false
  });

  const totalSteps = 10; // Added review step
  const progress = (step / totalSteps) * 100;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDirectorChange = (index, field, value) => {
    setFormData(prev => {
      const directors = [...prev.directors];
      directors[index] = { ...directors[index], [field]: value };
      return { ...prev, directors };
    });
  };

  const addDirector = () => {
    setFormData(prev => ({
      ...prev,
      directors: [...prev.directors, {
        full_name: "", id_number: "", position: "", phone: "", email: "", residential_address: "", ownership_percentage: ""
      }]
    }));
  };

  const handleExistingLoanChange = (index, field, value) => {
    setFormData(prev => {
      const loans = [...prev.existing_loans];
      loans[index] = { ...loans[index], [field]: value };
      return { ...prev, existing_loans: loans };
    });
  };

  const addExistingLoan = () => {
    setFormData(prev => ({
      ...prev,
      existing_loans: [...prev.existing_loans, {
        lender: "", loan_type: "", original_amount: "", current_balance: "", monthly_repayment: "", maturity_date: ""
      }]
    }));
  };

  const handleCollateralChange = (index, field, value) => {
    setFormData(prev => {
      const items = [...prev.collateral_items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, collateral_items: items };
    });
  };

  const addCollateralItem = () => {
    setFormData(prev => ({
      ...prev,
      collateral_items: [...prev.collateral_items, {
        asset_type: "", description: "", registered_owner: "", registration_number: "", estimated_value: "", location: ""
      }]
    }));
  };

  const handleReferenceChange = (index, field, value) => {
    setFormData(prev => {
      const refs = [...prev.trade_references];
      refs[index] = { ...refs[index], [field]: value };
      return { ...prev, trade_references: refs };
    });
  };

  const addReference = () => {
    setFormData(prev => ({
      ...prev,
      trade_references: [...prev.trade_references, {
        company_name: "", contact_person: "", phone: "", relationship: "", years_known: ""
      }]
    }));
  };

  const handleFileUpload = async (categoryId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    
    setUploadingDoc(categoryId);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocuments(prev => ({
          ...prev,
          [categoryId]: {
            name: file.name,
            size: file.size,
            type: file.type,
            data: reader.result,
            uploadedAt: new Date().toISOString()
          }
        }));
        toast.success(`${file.name} uploaded`);
        setUploadingDoc(null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Upload failed");
      setUploadingDoc(null);
    }
  };

  const removeDocument = (categoryId) => {
    setDocuments(prev => {
      const updated = { ...prev };
      delete updated[categoryId];
      return updated;
    });
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        return formData.business_name && formData.company_reg_number && formData.business_type;
      case 2:
        return formData.directors[0].full_name && formData.directors[0].id_number;
      case 3:
        return formData.loan_product && formData.amount_required && (formData.tenure_months || formData.custom_tenure) && formData.purpose_of_loan;
      case 4:
        return formData.annual_revenue && formData.monthly_expenses;
      case 5:
        return true; // Existing loans optional
      case 6:
        return formData.bank_name && formData.account_number;
      case 7:
        return formData.collateral_offered === "no" || (formData.collateral_items[0].asset_type && formData.collateral_items[0].estimated_value);
      case 8:
        return Object.keys(documents).length >= 4;
      case 9:
        return true; // Review step - always valid
      case 10:
        return formData.declaration_agreed && formData.terms_agreed && formData.consent_identity_verification && formData.consent_credit_bureau;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!formData.declaration_agreed || !formData.terms_agreed) {
      toast.error("Please agree to all declarations");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        applicant_type: "business",
        loan_type: formData.loan_product,
        amount: parseFloat(formData.amount_required) || 0,
        term_months: formData.tenure_months === "custom" 
          ? parseInt(formData.custom_tenure) || 12 
          : parseInt(formData.tenure_months) || 12,
        documents_meta: Object.keys(documents).reduce((acc, key) => {
          acc[key] = {
            name: documents[key].name,
            size: documents[key].size,
            type: documents[key].type,
            uploadedAt: documents[key].uploadedAt
          };
          return acc;
        }, {})
      };

      const response = await axios.post(
        `${API}/business/apply`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const appId = response.data.application_id;
      setApplicationId(appId);

      // Upload documents
      if (Object.keys(documents).length > 0) {
        try {
          await axios.post(
            `${API}/documents/upload`,
            { application_id: appId, documents },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (docError) {
          console.error("Document upload error:", docError);
        }
      }

      toast.success("Application submitted successfully!");
      setStep(totalSteps + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return "Business Information";
      case 2: return "Directors / Owners";
      case 3: return "Loan Requirements";
      case 4: return "Business Financials";
      case 5: return "Existing Obligations";
      case 6: return "Banking Details";
      case 7: return "Security / Collateral";
      case 8: return "Supporting Documents";
      case 9: return "Review Application";
      case 10: return "Declaration & Consent";
      default: return "Application Complete";
    }
  };

  const getStepIcon = () => {
    switch (step) {
      case 1: return Building2;
      case 2: return Users;
      case 3: return DollarSign;
      case 4: return FileText;
      case 5: return Briefcase;
      case 6: return Building2;
      case 7: return Shield;
      case 8: return Upload;
      case 9: return ClipboardCheck;
      case 10: return CheckCircle;
      default: return CheckCircle;
    }
  };

  const StepIcon = getStepIcon();

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Registered Business Name *</Label>
                <Input
                  value={formData.business_name}
                  onChange={(e) => handleInputChange("business_name", e.target.value)}
                  placeholder="Enter registered business name"
                />
              </div>
              <div>
                <Label>Trading Name (if different)</Label>
                <Input
                  value={formData.trading_name}
                  onChange={(e) => handleInputChange("trading_name", e.target.value)}
                  placeholder="Trading as..."
                />
              </div>
              <div>
                <Label>Company Registration Number *</Label>
                <Input
                  value={formData.company_reg_number}
                  onChange={(e) => handleInputChange("company_reg_number", e.target.value)}
                  placeholder="e.g., 1234/2020"
                />
              </div>
              <div>
                <Label>Date of Incorporation</Label>
                <Input
                  type="date"
                  value={formData.date_incorporated}
                  onChange={(e) => handleInputChange("date_incorporated", e.target.value)}
                />
              </div>
              <div>
                <Label>Business Type *</Label>
                <Select value={formData.business_type} onValueChange={(v) => handleInputChange("business_type", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Industry Sector</Label>
                <Input
                  value={formData.industry_sector}
                  onChange={(e) => handleInputChange("industry_sector", e.target.value)}
                  placeholder="e.g., Retail, Manufacturing"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Registered Address</Label>
                <Textarea
                  value={formData.registered_address}
                  onChange={(e) => handleInputChange("registered_address", e.target.value)}
                  placeholder="Enter full registered address"
                  rows={2}
                />
              </div>
              <div className="md:col-span-2">
                <Label>Physical/Trading Address</Label>
                <Textarea
                  value={formData.physical_address}
                  onChange={(e) => handleInputChange("physical_address", e.target.value)}
                  placeholder="Enter physical business address"
                  rows={2}
                />
              </div>
              <div>
                <Label>Property Ownership</Label>
                <div className="flex gap-4 mt-2">
                  {["owned", "rented", "leased"].map(opt => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="property_ownership"
                        value={opt}
                        checked={formData.property_ownership === opt}
                        onChange={(e) => handleInputChange("property_ownership", e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className="capitalize">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Years in Business</Label>
                <Input
                  type="number"
                  value={formData.years_in_business}
                  onChange={(e) => handleInputChange("years_in_business", e.target.value)}
                  placeholder="e.g., 5"
                />
              </div>
              <div>
                <Label>Telephone</Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => handleInputChange("telephone", e.target.value)}
                  placeholder="+263..."
                />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input
                  value={formData.mobile}
                  onChange={(e) => handleInputChange("mobile", e.target.value)}
                  placeholder="+263..."
                />
              </div>
              <div>
                <Label>Email Address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="business@email.com"
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => handleInputChange("website", e.target.value)}
                  placeholder="www.business.com"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <p className="text-sm text-gray-600 mb-4">Provide details of all directors/shareholders with 10% or more ownership</p>
            
            {formData.directors.map((director, index) => (
              <Card key={index} className="bg-gray-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Director/Owner {index + 1}</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={director.full_name}
                      onChange={(e) => handleDirectorChange(index, "full_name", e.target.value)}
                      placeholder="Full legal name"
                    />
                  </div>
                  <div>
                    <Label>National ID Number *</Label>
                    <Input
                      value={director.id_number}
                      onChange={(e) => handleDirectorChange(index, "id_number", e.target.value)}
                      placeholder="ID number"
                    />
                  </div>
                  <div>
                    <Label>Position/Title</Label>
                    <Input
                      value={director.position}
                      onChange={(e) => handleDirectorChange(index, "position", e.target.value)}
                      placeholder="e.g., Director, CEO"
                    />
                  </div>
                  <div>
                    <Label>Ownership %</Label>
                    <Input
                      type="number"
                      value={director.ownership_percentage}
                      onChange={(e) => handleDirectorChange(index, "ownership_percentage", e.target.value)}
                      placeholder="e.g., 50"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={director.phone}
                      onChange={(e) => handleDirectorChange(index, "phone", e.target.value)}
                      placeholder="+263..."
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={director.email}
                      onChange={(e) => handleDirectorChange(index, "email", e.target.value)}
                      placeholder="Email address"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Residential Address</Label>
                    <Textarea
                      value={director.residential_address}
                      onChange={(e) => handleDirectorChange(index, "residential_address", e.target.value)}
                      placeholder="Full residential address"
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
            
            <Button type="button" variant="outline" onClick={addDirector}>
              + Add Another Director/Owner
            </Button>

            <div className="border-t pt-6">
              <h4 className="font-medium mb-4">Primary Contact Person</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={formData.contact_person_name}
                    onChange={(e) => handleInputChange("contact_person_name", e.target.value)}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label>Position</Label>
                  <Input
                    value={formData.contact_person_position}
                    onChange={(e) => handleInputChange("contact_person_position", e.target.value)}
                    placeholder="e.g., Finance Manager"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={formData.contact_person_phone}
                    onChange={(e) => handleInputChange("contact_person_phone", e.target.value)}
                    placeholder="+263..."
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.contact_person_email}
                    onChange={(e) => handleInputChange("contact_person_email", e.target.value)}
                    placeholder="contact@business.com"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Loan Product *</Label>
                <Select value={formData.loan_product} onValueChange={(v) => handleInputChange("loan_product", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select loan product" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOAN_PRODUCTS.map(product => (
                      <SelectItem key={product.id} value={product.id}>
                        <div>
                          <span className="font-medium">{product.name}</span>
                          <span className="text-gray-500 text-sm ml-2">- {product.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Amount Required (USD) *</Label>
                <Input
                  type="number"
                  value={formData.amount_required}
                  onChange={(e) => handleInputChange("amount_required", e.target.value)}
                  placeholder="Enter loan amount"
                />
              </div>
              <div>
                <Label>Loan Tenure *</Label>
                <Select
                  value={formData.tenure_months}
                  onValueChange={(v) => {
                    handleInputChange("tenure_months", v);
                    if (v !== "custom") handleInputChange("custom_tenure", "");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tenure" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                    <SelectItem value="18">18 months</SelectItem>
                    <SelectItem value="24">24 months</SelectItem>
                    <SelectItem value="36">36 months</SelectItem>
                    <SelectItem value="48">48 months</SelectItem>
                    <SelectItem value="60">60 months</SelectItem>
                    <SelectItem value="custom">Custom tenure</SelectItem>
                  </SelectContent>
                </Select>
                {formData.tenure_months === "custom" && (
                  <div className="mt-2">
                    <Input
                      type="number"
                      value={formData.custom_tenure}
                      onChange={(e) => handleInputChange("custom_tenure", e.target.value)}
                      placeholder="Enter custom tenure in months"
                      min="1"
                      max="120"
                    />
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Purpose of Loan *</Label>
              <Textarea
                value={formData.purpose_of_loan}
                onChange={(e) => handleInputChange("purpose_of_loan", e.target.value)}
                placeholder="Describe how the loan will be used..."
                rows={3}
              />
            </div>

            <div>
              <Label>Source of Repayment</Label>
              <Textarea
                value={formData.repayment_source}
                onChange={(e) => handleInputChange("repayment_source", e.target.value)}
                placeholder="How will the loan be repaid? (e.g., business revenue, contract payments)"
                rows={2}
              />
            </div>

            <div>
              <Label>Preferred Disbursement Method</Label>
              <div className="flex gap-4 mt-2">
                {["Bank Transfer", "Cheque", "Mobile Money"].map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="preferred_disbursement"
                      value={opt}
                      checked={formData.preferred_disbursement === opt}
                      onChange={(e) => handleInputChange("preferred_disbursement", e.target.value)}
                      className="w-4 h-4"
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Annual Revenue (USD) *</Label>
                <Input
                  type="number"
                  value={formData.annual_revenue}
                  onChange={(e) => handleInputChange("annual_revenue", e.target.value)}
                  placeholder="Total annual revenue"
                />
              </div>
              <div>
                <Label>Average Monthly Revenue (USD)</Label>
                <Input
                  type="number"
                  value={formData.monthly_revenue}
                  onChange={(e) => handleInputChange("monthly_revenue", e.target.value)}
                  placeholder="Average monthly revenue"
                />
              </div>
              <div>
                <Label>Gross Profit Margin (%)</Label>
                <Input
                  type="number"
                  value={formData.gross_profit_margin}
                  onChange={(e) => handleInputChange("gross_profit_margin", e.target.value)}
                  placeholder="e.g., 35"
                />
              </div>
              <div>
                <Label>Monthly Operating Expenses (USD) *</Label>
                <Input
                  type="number"
                  value={formData.monthly_expenses}
                  onChange={(e) => handleInputChange("monthly_expenses", e.target.value)}
                  placeholder="Average monthly expenses"
                />
              </div>
              <div>
                <Label>Net Profit (Annual, USD)</Label>
                <Input
                  type="number"
                  value={formData.net_profit}
                  onChange={(e) => handleInputChange("net_profit", e.target.value)}
                  placeholder="Net profit after all expenses"
                />
              </div>
              <div>
                <Label>Number of Employees</Label>
                <Input
                  type="number"
                  value={formData.number_of_employees}
                  onChange={(e) => handleInputChange("number_of_employees", e.target.value)}
                  placeholder="Total employees"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <Label>Do you have any existing loans or credit facilities?</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="has_existing_loans"
                    value="yes"
                    checked={formData.has_existing_loans === "yes"}
                    onChange={(e) => handleInputChange("has_existing_loans", e.target.value)}
                    className="w-4 h-4"
                  />
                  <span>Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="has_existing_loans"
                    value="no"
                    checked={formData.has_existing_loans === "no"}
                    onChange={(e) => handleInputChange("has_existing_loans", e.target.value)}
                    className="w-4 h-4"
                  />
                  <span>No</span>
                </label>
              </div>
            </div>

            {formData.has_existing_loans === "yes" && (
              <div className="space-y-4">
                {formData.existing_loans.map((loan, index) => (
                  <Card key={index} className="bg-gray-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Existing Loan/Facility {index + 1}</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Lender Name</Label>
                        <Input
                          value={loan.lender}
                          onChange={(e) => handleExistingLoanChange(index, "lender", e.target.value)}
                          placeholder="Bank/Institution name"
                        />
                      </div>
                      <div>
                        <Label>Type of Facility</Label>
                        <Input
                          value={loan.loan_type}
                          onChange={(e) => handleExistingLoanChange(index, "loan_type", e.target.value)}
                          placeholder="e.g., Term Loan, Overdraft"
                        />
                      </div>
                      <div>
                        <Label>Original Amount (USD)</Label>
                        <Input
                          type="number"
                          value={loan.original_amount}
                          onChange={(e) => handleExistingLoanChange(index, "original_amount", e.target.value)}
                          placeholder="Original amount"
                        />
                      </div>
                      <div>
                        <Label>Current Balance (USD)</Label>
                        <Input
                          type="number"
                          value={loan.current_balance}
                          onChange={(e) => handleExistingLoanChange(index, "current_balance", e.target.value)}
                          placeholder="Outstanding balance"
                        />
                      </div>
                      <div>
                        <Label>Monthly Repayment (USD)</Label>
                        <Input
                          type="number"
                          value={loan.monthly_repayment}
                          onChange={(e) => handleExistingLoanChange(index, "monthly_repayment", e.target.value)}
                          placeholder="Monthly payment"
                        />
                      </div>
                      <div>
                        <Label>Maturity Date</Label>
                        <Input
                          type="date"
                          value={loan.maturity_date}
                          onChange={(e) => handleExistingLoanChange(index, "maturity_date", e.target.value)}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button type="button" variant="outline" onClick={addExistingLoan}>
                  + Add Another Facility
                </Button>
              </div>
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">Provide details of your primary business bank account</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Bank Name *</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => handleInputChange("bank_name", e.target.value)}
                  placeholder="e.g., CBZ Bank"
                />
              </div>
              <div>
                <Label>Branch</Label>
                <Input
                  value={formData.branch_name}
                  onChange={(e) => handleInputChange("branch_name", e.target.value)}
                  placeholder="Branch name"
                />
              </div>
              <div>
                <Label>Account Number *</Label>
                <Input
                  value={formData.account_number}
                  onChange={(e) => handleInputChange("account_number", e.target.value)}
                  placeholder="Account number"
                />
              </div>
              <div>
                <Label>Account Type</Label>
                <Select value={formData.account_type} onValueChange={(v) => handleInputChange("account_type", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select account type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Current Account</SelectItem>
                    <SelectItem value="savings">Savings Account</SelectItem>
                    <SelectItem value="corporate">Corporate Account</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Years with Bank</Label>
                <Input
                  type="number"
                  value={formData.years_with_bank}
                  onChange={(e) => handleInputChange("years_with_bank", e.target.value)}
                  placeholder="e.g., 3"
                />
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div>
              <Label>Will you be offering collateral/security?</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="collateral_offered"
                    value="yes"
                    checked={formData.collateral_offered === "yes"}
                    onChange={(e) => handleInputChange("collateral_offered", e.target.value)}
                    className="w-4 h-4"
                  />
                  <span>Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="collateral_offered"
                    value="no"
                    checked={formData.collateral_offered === "no"}
                    onChange={(e) => handleInputChange("collateral_offered", e.target.value)}
                    className="w-4 h-4"
                  />
                  <span>No (Unsecured)</span>
                </label>
              </div>
            </div>

            {formData.collateral_offered === "yes" && (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      Collateral value should typically be at least 1.5x the loan amount requested.
                    </p>
                  </div>
                </div>

                {formData.collateral_items.map((item, index) => (
                  <Card key={index} className="bg-gray-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Collateral Item {index + 1}</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Asset Type *</Label>
                        <Select 
                          value={item.asset_type} 
                          onValueChange={(v) => handleCollateralChange(index, "asset_type", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select asset type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="property">Property/Real Estate</SelectItem>
                            <SelectItem value="vehicle">Motor Vehicle</SelectItem>
                            <SelectItem value="equipment">Equipment/Machinery</SelectItem>
                            <SelectItem value="inventory">Inventory/Stock</SelectItem>
                            <SelectItem value="receivables">Accounts Receivable</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Estimated Value (USD) *</Label>
                        <Input
                          type="number"
                          value={item.estimated_value}
                          onChange={(e) => handleCollateralChange(index, "estimated_value", e.target.value)}
                          placeholder="Market value"
                        />
                      </div>
                      <div>
                        <Label>Registered Owner</Label>
                        <Input
                          value={item.registered_owner}
                          onChange={(e) => handleCollateralChange(index, "registered_owner", e.target.value)}
                          placeholder="Owner name"
                        />
                      </div>
                      <div>
                        <Label>Registration/Serial Number</Label>
                        <Input
                          value={item.registration_number}
                          onChange={(e) => handleCollateralChange(index, "registration_number", e.target.value)}
                          placeholder="e.g., Vehicle reg, Title deed"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label>Description</Label>
                        <Textarea
                          value={item.description}
                          onChange={(e) => handleCollateralChange(index, "description", e.target.value)}
                          placeholder="Detailed description of the asset"
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>Location</Label>
                        <Input
                          value={item.location}
                          onChange={(e) => handleCollateralChange(index, "location", e.target.value)}
                          placeholder="Where is the asset located?"
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
                <Button type="button" variant="outline" onClick={addCollateralItem}>
                  + Add Another Asset
                </Button>
              </>
            )}

            <div className="border-t pt-6">
              <h4 className="font-medium mb-4">Trade References (Optional)</h4>
              {formData.trade_references.map((ref, index) => (
                <Card key={index} className="bg-gray-50 mb-4">
                  <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company Name</Label>
                      <Input
                        value={ref.company_name}
                        onChange={(e) => handleReferenceChange(index, "company_name", e.target.value)}
                        placeholder="Reference company"
                      />
                    </div>
                    <div>
                      <Label>Contact Person</Label>
                      <Input
                        value={ref.contact_person}
                        onChange={(e) => handleReferenceChange(index, "contact_person", e.target.value)}
                        placeholder="Contact name"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={ref.phone}
                        onChange={(e) => handleReferenceChange(index, "phone", e.target.value)}
                        placeholder="+263..."
                      />
                    </div>
                    <div>
                      <Label>Relationship</Label>
                      <Input
                        value={ref.relationship}
                        onChange={(e) => handleReferenceChange(index, "relationship", e.target.value)}
                        placeholder="e.g., Supplier, Customer"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addReference}>
                + Add Reference
              </Button>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Upload required supporting documents. Accepted formats: PDF, Word, Images (max 10MB each).
            </p>
            
            <div className="grid grid-cols-1 gap-3">
              {DOCUMENT_CATEGORIES.map((category) => (
                <div 
                  key={category.id}
                  className={`border rounded-lg p-4 ${
                    documents[category.id] ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className={`h-5 w-5 ${documents[category.id] ? 'text-emerald-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="font-medium text-sm">
                          {category.name}
                          {category.required && <span className="text-red-500 ml-1">*</span>}
                        </p>
                        {documents[category.id] && (
                          <p className="text-xs text-emerald-600">
                            {documents[category.id].name} ({formatFileSize(documents[category.id].size)})
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {documents[category.id] ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-emerald-500" />
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeDocument(category.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileUpload(category.id, e.target.files?.[0])}
                          disabled={uploadingDoc === category.id}
                        />
                        <Button variant="outline" size="sm" className="pointer-events-none">
                          {uploadingDoc === category.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload
                            </>
                          )}
                        </Button>
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">
                Documents Uploaded: {Object.keys(documents).length} / {DOCUMENT_CATEGORIES.length}
              </p>
            </div>
          </div>
        );

      case 9:
        // Review Step
        const getTenureDisplay = () => {
          if (formData.tenure_months === "custom") return `${formData.custom_tenure} months (custom)`;
          return `${formData.tenure_months} months`;
        };
        
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-blue-800 font-medium">Review Your Application</p>
                  <p className="text-blue-700 text-sm mt-1">
                    Please review all information carefully before proceeding to submit.
                  </p>
                </div>
              </div>
            </div>

            {/* Business Information Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Business Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Business Name:</span>
                  <p className="font-medium">{formData.business_name || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Registration No:</span>
                  <p className="font-medium">{formData.company_reg_number || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Business Type:</span>
                  <p className="font-medium">{formData.business_type || "N/A"}</p>
                </div>
                <div>
                  <span className="text-gray-500">Years in Business:</span>
                  <p className="font-medium">{formData.years_in_business || "N/A"}</p>
                </div>
              </CardContent>
            </Card>

            {/* Loan Details Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Loan Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Loan Product:</span>
                  <p className="font-medium">{LOAN_PRODUCTS.find(p => p.id === formData.loan_product)?.name || formData.loan_product}</p>
                </div>
                <div>
                  <span className="text-gray-500">Amount Requested:</span>
                  <p className="font-medium text-green-700">${parseFloat(formData.amount_required || 0).toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Loan Tenure:</span>
                  <p className="font-medium">{getTenureDisplay()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Purpose:</span>
                  <p className="font-medium">{formData.purpose_of_loan?.substring(0, 50) || "N/A"}...</p>
                </div>
              </CardContent>
            </Card>

            {/* Financial Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Financial Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Annual Revenue:</span>
                  <p className="font-medium">${parseFloat(formData.annual_revenue || 0).toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">Monthly Expenses:</span>
                  <p className="font-medium">${parseFloat(formData.monthly_expenses || 0).toLocaleString()}</p>
                </div>
              </CardContent>
            </Card>

            {/* Documents Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Documents Uploaded
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">
                  <span className="font-medium text-green-700">{Object.keys(documents).length}</span> document(s) uploaded
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 10:
        return (
          <div className="space-y-6">
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-lg">Declaration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-gray-700">
                <p>
                  I/We hereby declare that the information provided in this application is true, complete, 
                  and accurate to the best of my/our knowledge. I/We understand that any misrepresentation 
                  or omission may result in the rejection of this application or immediate recall of any 
                  facility granted.
                </p>
                <p>
                  I/We authorize Tzur Finance (Pvt) Ltd to make inquiries as deemed necessary, including 
                  but not limited to credit bureaus, banks, suppliers, and any other relevant parties.
                </p>
                <p>
                  I/We understand and accept that Tzur Finance (Pvt) Ltd reserves the right to approve 
                  or decline this application at its sole discretion.
                </p>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer p-4 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  checked={formData.consent_identity_verification}
                  onCheckedChange={(checked) => handleInputChange("consent_identity_verification", checked)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  I consent to Tzur Finance (Pvt) Ltd verifying my identity through relevant government 
                  databases and third-party verification services. *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-4 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  checked={formData.consent_credit_bureau}
                  onCheckedChange={(checked) => handleInputChange("consent_credit_bureau", checked)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  I consent to Tzur Finance (Pvt) Ltd conducting credit checks through registered credit bureaus 
                  and accessing my credit history for the purpose of assessing this loan application. *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-4 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  checked={formData.declaration_agreed}
                  onCheckedChange={(checked) => handleInputChange("declaration_agreed", checked)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  I declare that all information provided is true and accurate. *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-4 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  checked={formData.terms_agreed}
                  onCheckedChange={(checked) => handleInputChange("terms_agreed", checked)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  I agree to the Terms and Conditions and authorize credit checks. *
                </span>
              </label>
            </div>

            {(!formData.consent_identity_verification || !formData.consent_credit_bureau || !formData.declaration_agreed || !formData.terms_agreed) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Please provide all required consents and agree to the declaration to submit your application.
                  </span>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h3 className="text-2xl font-bold text-[#0A2540] mb-2">Application Submitted!</h3>
            <p className="text-gray-600 mb-4">
              Your Business Loan application has been received.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Application ID: <span className="font-mono font-semibold">{applicationId}</span>
            </p>
            <p className="text-sm text-gray-600 mb-8">
              Our team will review your application and contact you within 24-48 hours.
            </p>
            <Link to="/dashboard">
              <Button className="bg-[#0A2540] hover:bg-[#0A2540]/90">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        );
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader className="text-center">
            <CardTitle>Login Required</CardTitle>
            <CardDescription>Please sign in to apply for a Business Loan</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Link to="/login">
              <Button className="w-full">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="w-full">Create Account</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-white rounded-lg px-2 py-1 shadow-sm border border-gray-100">
                <img src={LOGO_URL} alt="Tzur Finances" className="h-8 object-contain" />
              </div>
            </Link>
            <Link to="/products">
              <Button variant="ghost" className="text-gray-600">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Products
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      {step <= totalSteps && (
        <div className="bg-white border-b">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Step {step} of {totalSteps}</span>
              <span className="text-sm text-gray-500">{Math.round(progress)}% Complete</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#0A2540] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {step <= totalSteps && (
          <div className="mb-8 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#0A2540]/10 mb-4">
              <StepIcon className="h-7 w-7 text-[#0A2540]" />
            </div>
            <h2 className="text-2xl font-bold text-[#0A2540]">{getStepTitle()}</h2>
            <p className="text-gray-500 mt-1">Business Loan Application</p>
          </div>
        )}

        <Card className="shadow-sm">
          <CardContent className="p-6">
            {renderStep()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        {step <= totalSteps && (
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            {step < totalSteps ? (
              <Button onClick={handleNext} className="bg-[#0A2540] hover:bg-[#0A2540]/90">
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={loading || !formData.declaration_agreed || !formData.terms_agreed}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Submit Application
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default BusinessLoanApplication;
